import { ProblemResults, StudentResults } from '@yandex-int/k-common/typings';
import b from 'b_';
import cx from 'classnames';
import { MarkerDiskUrlShowButtonProvider } from 'platform-components/src/components/marker-diskurl/context';
import { getIsParent } from 'platform-components/src/components/user/selectors';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUnmount } from 'react-use';
import { useLocation, useHistory } from 'react-router-dom';

import { lessonContentActions } from 'common.components/lesson-content/actions';
import * as lessonSelectors from 'common.components/lesson/selectors';
import { LessonProblemWithResult } from 'common.components/student-lesson-stat/typings';
import { getProblemDisplayStatus } from 'common.components/student-lesson-stat/utils';

import ItemWrapper from './__item-wrapper/lesson-content__item-wrapper';
import { LessonContentProblemModal } from './__problem-modal/lesson-content__problem-modal';
import { LessonContentProblemOverlay } from './__problem-overlay/lesson-content__problem-overlay';
import { LessonContentProblem } from './__problem/lesson-content__problem';
import ContentRow from './__row/lesson-content__row';
import { useStudentAttemptsText } from './__stat-item/_student-attempts-text/use-student-attempts-text';
import ProblemStats from './__stat/lesson-content__stat';
import { getShouldShowProblemOverlay } from './utils';

import './lesson-content.scss';

interface Props {
    fullScreen: boolean;
    isEvaluable: boolean;
    isEvaluated: boolean;
    correctAnswersAvailable: boolean;
    problems: Array<LessonProblemWithResult>;
    mix?: string;
    studentResults: StudentResults | null;
    lessonProblemResults: Record<number, ProblemResults>;
    isPointsMode: boolean;
    shouldDisplayAttempts: boolean;
    studentId?: number | null;
}

function LessonContent(props: Props) {
    const {
        fullScreen,
        mix,
        problems,
        studentResults,
        correctAnswersAvailable,
        lessonProblemResults,
        isEvaluable,
        isEvaluated,
        isPointsMode,
        shouldDisplayAttempts,
        studentId,
    } = props;

    const isParent = useSelector(getIsParent);
    const isLessonScoreAvailable = useSelector(lessonSelectors.getIsLessonScoreAvailable);

    const dispatch = useDispatch();
    const expandProblem = useCallback(
        (problemId: number) => dispatch(lessonContentActions.expandProblem({ problemId })),
        [dispatch]
    );
    useUnmount(() => dispatch(lessonContentActions.reset()));
    const renderStudentAttemptsText = useStudentAttemptsText();

    const { search, pathname } = useLocation();
    const history = useHistory();
    useEffect(() => {
        const queryParams = new URLSearchParams(search);
        const problemId = queryParams.get('problemId');
        if (problemId) {
            queryParams.delete('problemId');
            const problem = problems.find((aProblem) => aProblem.problem.id === Number(problemId));
            if (problem) {
                expandProblem(problem.id);
            }
            history.replace(pathname + '?' + queryParams.toString());
        }
    }, [search, history, pathname, dispatch, problems, expandProblem]);

    const className = b('lesson-content', {
        'full-screen': fullScreen,
        'with-controls': true,
        'with-stats': Boolean(studentResults),
    });

    return (
        <MarkerDiskUrlShowButtonProvider>
            <div className={cx(className, mix)}>
                {problems.map((itemLink: LessonProblemWithResult, index: number) => {
                    const problemId = itemLink?.problem?.id;
                    const studentProblemResult = studentResults?.problems[itemLink?.id] || null;
                    const itemStatus = getProblemDisplayStatus(itemLink, studentProblemResult);
                    const lessonProblemResult = lessonProblemResults[itemLink?.id];
                    const problem = itemLink.problem;
                    const showOverlay = getShouldShowProblemOverlay(problem);

                    return (
                        <ContentRow fullScreen={fullScreen} key={`row-${problemId}`}>
                            <div className="lesson-content__item-col">
                                <ItemWrapper
                                    fullScreen={fullScreen}
                                    onExpand={() => expandProblem(itemLink.id)}
                                    status={itemStatus}
                                >
                                    <LessonContentProblem
                                        correctAnswersAvailable={correctAnswersAvailable}
                                        itemLink={itemLink}
                                        mix={cx('lesson-content__lesson-item', 'lesson-content__preview-problem')}
                                    />
                                    {showOverlay && <LessonContentProblemOverlay />}
                                </ItemWrapper>
                            </div>
                            <ProblemStats
                                correctAnswersAvailable={correctAnswersAvailable}
                                fullScreen={fullScreen}
                                index={index}
                                isEvaluable={isEvaluable}
                                isEvaluated={isEvaluated}
                                isLessonScoreAvailable={isLessonScoreAvailable}
                                isParent={isParent}
                                isPointsMode={isPointsMode}
                                lessonProblemResult={lessonProblemResult}
                                problemLink={itemLink}
                                renderStudentAttemptsText={renderStudentAttemptsText}
                                shouldDisplayAttempts={shouldDisplayAttempts}
                                studentProblemResult={studentProblemResult}
                            />
                        </ContentRow>
                    );
                })}
            </div>
            <LessonContentProblemModal
                correctAnswersAvailable={correctAnswersAvailable}
                problems={problems}
                studentId={studentId}
                studentResults={studentResults}
            />
        </MarkerDiskUrlShowButtonProvider>
    );
}

export default LessonContent;
